/**
 * StarRating Component
 *
 * Animated star rating display.
 * Features:
 * - Stars fill in sequentially with animation
 * - Supports partial stars (4.5 rating)
 * - Optional review count display
 * - Configurable colors and sizes
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

export interface StarRatingProps {
  /** Rating value (0-5) */
  rating: number;
  /** Number of reviews to display */
  reviewCount?: number;
  /** Frame when animation should start */
  startFrame?: number;
  /** Color for filled stars */
  filledColor?: string;
  /** Color for empty stars */
  emptyColor?: string;
  /** Color for review count text */
  textColor?: string;
  /** Star size */
  starSize?: number;
  /** Gap between stars */
  gap?: number;
  /** Animation duration per star (in seconds) */
  animationDurationPerStar?: number;
}

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  reviewCount,
  startFrame = 0,
  filledColor = '#FFD700',
  emptyColor = 'rgba(255,255,255,0.2)',
  textColor = '#ffffff',
  starSize = 28,
  gap = 4,
  animationDurationPerStar = 0.15,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Calculate how many stars should be visible at current frame
  const framesPerStar = fps * animationDurationPerStar;
  const totalAnimationFrames = framesPerStar * 5;

  // Overall opacity fade in
  const opacity = interpolate(
    relativeFrame,
    [0, fps * 0.3],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  const renderStar = (index: number) => {
    const starStartFrame = index * framesPerStar;

    // Star fill animation
    const fillProgress = interpolate(
      relativeFrame,
      [starStartFrame, starStartFrame + framesPerStar],
      [0, 1],
      { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    // Calculate if this star should be filled based on rating
    const starValue = index + 1;
    const isFull = rating >= starValue;
    const isPartial = rating > index && rating < starValue;
    const partialAmount = isPartial ? rating - index : 0;

    // Scale pop animation when star fills
    const scale = spring({
      frame: relativeFrame - starStartFrame,
      fps,
      from: 0.5,
      to: 1,
      durationInFrames: framesPerStar,
      config: {
        stiffness: 300,
        damping: 15,
      },
    });

    // Determine fill percentage
    let fillPercentage = 0;
    if (isFull) {
      fillPercentage = fillProgress * 100;
    } else if (isPartial) {
      fillPercentage = fillProgress * partialAmount * 100;
    }

    return (
      <div
        key={index}
        style={{
          position: 'relative',
          width: starSize,
          height: starSize,
          transform: `scale(${fillProgress > 0 ? scale : 1})`,
        }}
      >
        {/* Empty star background */}
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: starSize,
            color: emptyColor,
            lineHeight: 1,
          }}
        >
          ★
        </span>
        {/* Filled star with clip */}
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: starSize,
            color: filledColor,
            lineHeight: 1,
            clipPath: `inset(0 ${100 - fillPercentage}% 0 0)`,
          }}
        >
          ★
        </span>
      </div>
    );
  };

  // Review count fade in after stars
  const reviewOpacity = interpolate(
    relativeFrame,
    [totalAnimationFrames, totalAnimationFrames + fps * 0.3],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: gap * 2,
        opacity,
      }}
    >
      {/* Stars container */}
      <div
        style={{
          display: 'flex',
          gap,
        }}
      >
        {[0, 1, 2, 3, 4].map(renderStar)}
      </div>

      {/* Rating number */}
      <span
        style={{
          color: textColor,
          fontSize: starSize * 0.75,
          fontWeight: 600,
          fontFamily: 'Inter, sans-serif',
          opacity: reviewOpacity,
        }}
      >
        {rating.toFixed(1)}
      </span>

      {/* Review count */}
      {reviewCount !== undefined && (
        <span
          style={{
            color: `${textColor}99`, // Add transparency
            fontSize: starSize * 0.6,
            fontFamily: 'Inter, sans-serif',
            opacity: reviewOpacity,
          }}
        >
          ({reviewCount.toLocaleString()} reviews)
        </span>
      )}
    </div>
  );
};
